import * as moment from "moment-timezone";
import { useCallback } from "react";

import { productCutOffTime } from "src/core/orders/utils/order-deliveries";
import useTimezone from "src/core/shared/hooks/time-helpers/useTimeZone";
import { IOrderStatus } from "src/core/orders/api/orders.api";
import { IOrderLineError } from "src/core/errors/api/errors.api";
import { WeekDay } from "src/core/drops/api/drops.api";
import { useProductLeadTimeGroup } from "src/core/products/hooks/useProductLeadTimeGroup";

import { IOrderLine } from "../../api/orders.api";
import useCutoffTimeForWeekDay from "./useCutoffTimeForWeekDay";

/**
 * Check if an order is before cutoff
 *
 * @param {IOrder} order
 * @returns {(string | null)}
 */
const useOrderLineError = () => {
  const cutoffTimeForWeekDay = useCutoffTimeForWeekDay();
  const timezone = useTimezone();
  const productLeadTimeGroup = useProductLeadTimeGroup();

  const orderLineError = useCallback(
    (orderLine: IOrderLine, deliveryDate: Date | null, orderStatus?: IOrderStatus): IOrderLineError[] => {
      const productLeadTime = productLeadTimeGroup(orderLine.product);

      // if theres a server error on the order line then just use this
      if (orderLine.errors && orderLine.errors.length > 1) {
        return orderLine.errors;
      } else if (deliveryDate && productLeadTime) {
        const cutoff = cutoffTimeForWeekDay(productLeadTime, moment.utc(deliveryDate).isoWeekday() as WeekDay);

        if (cutoff && moment.utc().isAfter(productCutOffTime(productLeadTime, cutoff, deliveryDate, timezone))) {
          if (orderStatus && (orderStatus.settings.group === "DRAFT" || orderStatus.settings.group === "ERROR")) {
            return [
              {
                createdAt: new Date(),
                updatedAt: new Date(),
                note: "Product not available for this date!",
                code: "product-not-available",
                entityId: orderLine.orderId as number,
                type: "order",
                secondaryEntityId: orderLine.id,
                secondaryType: "order-line",
                status: "active",
                id: 0,
                errorCode: {
                  id: 0,
                  originatedFrom: "ui",
                  note: "",
                  description: "",
                  code: "product-not-available",
                  type: "order"
                }
              }
            ];
          }
        }
      }

      return [];
    },
    [
      productLeadTimeGroup,
      cutoffTimeForWeekDay,
      timezone
    ]
  );

  return orderLineError;
};

export default useOrderLineError;
